/**
 * `diceController` — the app-wide seam for "throw some dice." It's a tiny pub/sub the
 * {@link ./DiceOverlay} subscribes to; anything in the app can request a throw and make dice
 * tumble across the global overlay, from any route, without threading props or context.
 *
 * Two request shapes:
 *   - `{ kind: "d20" }` — a physics-decides dev d20 (the value is whatever it lands on). Kept
 *     for console iteration on the physics/visuals; not a real roll.
 *   - `{ kind: "roll", result }` — **the real contract.** Carries a `RollResult` from
 *     `@lancer-vtt/rules` (the single source of truth for *what was rolled*); the overlay
 *     choreographs one die per recorded draw, landing each on its recorded face, and shows the
 *     readout. The controller never decides a roll — it only carries one the authority produced.
 *
 * In dev it hangs `window.__lvttDice` so throws can be triggered from the console while iterating.
 *
 * **Every throw is awaitable**: each `throw*` returns a `Promise<void>` that resolves when *that
 * throw's* dice have stopped rolling, so a caller can hold an outcome back until the table has
 * seen the roll. The promise rides beside the request as a {@link ThrowTicket} rather than inside
 * it, which keeps {@link DiceThrowRequest} — the shape `projectThrow` and the dev hook speak —
 * unchanged.
 *
 * The awaiting contract has one rule, and it is what protects the isolation invariant this
 * pub/sub exists for (`DiceFeedSubscriber.tsx`: "Remove this one component and COMMS.app + the
 * feed are fully functional"): **a throw nobody claims resolves immediately.** No subscriber, no
 * WebGL stage, a pool over the visualization cap, a resolution that rolled no dice at all — every
 * one of those is "no dice are coming," and the awaiting caller proceeds at once instead of
 * waiting on a tumble that will never happen. See {@link ./throwSettlement}.
 */
import { roll, scriptedDiceSource, type RollResult } from "@lancer-vtt/rules";
import type { ResolvedStep } from "@lancer-vtt/domain";
import type { RollFeedEntry } from "@lancer-vtt/shared";
import { diceSpecsFromSteps } from "./resolutionDice";
import { createThrowTicket, type ThrowTicket } from "./throwSettlement";

export type DiceThrowRequest =
  /** Dev-only: a single physics-decides d20. */
  | { kind: "d20" }
  /** The real contract: visualize a recorded `RollResult` (one die per draw, on its face). */
  | { kind: "roll"; result: RollResult }
  /** A combat **resolution** (recorded `ResolvedStep[]`) — the real authoritative roll sites. */
  | { kind: "steps"; steps: ResolvedStep[] }
  /** An ad-hoc `/roll` from chat's authoritative feed (one die per recorded draw, on its face). */
  | { kind: "feedRoll"; entry: RollFeedEntry };

/**
 * A throw request plus its settle channel. The `ticket` is the second argument rather than a
 * field on the request so `DiceThrowRequest` stays the plain, projectable shape it always was.
 * A listener that is really going to land dice must `claim()` it synchronously — see the file
 * header.
 */
type Listener = (req: DiceThrowRequest, ticket: ThrowTicket) => void;

const listeners = new Set<Listener>();

/** Subscribe to throw requests; returns an unsubscribe fn. */
export function subscribeDiceThrows(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Emit a throw and hand back the promise for its dice settling. The claim check runs after the
 * synchronous fan-out: by then every listener has had its chance to take responsibility, and a
 * ticket still unclaimed means no dice are coming for this throw.
 */
function emit(req: DiceThrowRequest): Promise<void> {
  const { ticket, done } = createThrowTicket();
  for (const listener of listeners) listener(req, ticket);
  if (!ticket.claimed) ticket.settle();
  return done;
}

/** Request a single physics-decides d20 throw on the global overlay (dev). */
export function throwD20(): Promise<void> {
  return emit({ kind: "d20" });
}

/** Visualize an authoritative `RollResult` on the global overlay — dice land on its recorded faces. */
export function throwRoll(result: RollResult): Promise<void> {
  return emit({ kind: "roll", result });
}

/**
 * Visualize a combat **resolution** (the recorded `ResolvedStep[]` a real attack/save/table-check
 * broadcasts) on the global overlay. A no-op when the resolution rolled no dice (a pure move /
 * status application) — so the overlay never spins up for a roll-less resolution, and the
 * returned promise is already resolved: there is nothing to wait for.
 */
export function throwSteps(steps: ResolvedStep[]): Promise<void> {
  if (diceSpecsFromSteps(steps).length === 0) return Promise.resolve();
  return emit({ kind: "steps", steps });
}

/**
 * Visualize an ad-hoc `/roll` from chat's authoritative feed (a {@link RollFeedEntry}) — the
 * overlay tumbles one die per recorded draw onto its recorded face, with the matching total.
 * The controller only carries the server's record; the overlay projects it ({@link diceSpecsFromFeedRoll}).
 * This is the **only** dice path for `/roll` — there's no second roll command or RNG; chat owns
 * the authority, the dice side just visualizes it.
 */
export function throwFeedRoll(entry: RollFeedEntry): Promise<void> {
  return emit({ kind: "feedRoll", entry });
}

declare global {
  // eslint-disable-next-line no-var
  var __lvttDice:
    | {
        throwD20: () => Promise<void>;
        throwRoll: (result: RollResult) => Promise<void>;
        throwSteps: (steps: ResolvedStep[]) => Promise<void>;
        demo: () => Promise<void>;
        demoSteps: () => Promise<void>;
      }
    | undefined;
}

/** Install the dev console hook (`window.__lvttDice`). Idempotent. */
export function installDiceDevHook(): void {
  if (typeof globalThis !== "undefined" && import.meta.env?.DEV) {
    globalThis.__lvttDice = {
      throwD20,
      throwRoll,
      throwSteps,
      // A canned roll so a real choreographed throw (with readout) can be eyeballed from the
      // console: a d20=14 with +2 accuracy [2,5] → lands a 14, two extra d6, reads "d20 +5acc = 19".
      demo: () => throwRoll(roll({ count: 1, sides: 20, accuracy: 2 }, scriptedDiceSource([14, 2, 5]))),
      // A canned **resolution**: a crit to-hit (d20 + acc d6) + a 3-die d6 damage pool — exercises
      // the steps path with the real d20/d6 meshes side by side.
      demoSteps: () =>
        throwSteps([
          { op: "to_hit", attacker: "A", target: "B", d20: 14, accuracy: 5, bonus: 3, total: 22, vs: "evasion", defense: 10, result: "crit" },
          { op: "damage", dtype: "kinetic", dice: [6, 4, 5], bonus: 2, preMitigation: 17, ap: false, armor: 0, resisted: false, final: 17, crit: true },
        ]),
    };
  }
}
